import { useId, useState } from 'react';
import {
  ChartNoAxesColumnIncreasing,
  Check,
  HeartPulse,
  Image as ImageIcon,
  Megaphone,
  Palette,
  ShieldCheck,
  Sprout,
} from 'lucide-react';
import Screen from '../../demo/Screen';
import FitToWidth from '../../ui/FitToWidth';
import Organ from '../../mocks/Organs';
import { LeafSprig, MountainLake } from '../../mocks/Scenery';
import './BrandEquity.css';

/*
 * Brand colours a lab might pick. They recolour only the middle "Smart Health
 * Report" cover: `accent` fills its leaves and emblem, `ink` its pointer icons
 * and `deep` its title. The two side pages keep the default palette, and the
 * cover's body text stays standard. Green is the primary (listed first and
 * applied on load): a rich NirogGyan green. Status colours never change.
 */
export const BRAND_COLOURS = [
  { key: 'green', name: 'Green', accent: '#17a058', ink: '#0f7d43', deep: '#0a4a2a' },
  { key: 'blue', name: 'Blue', accent: '#1463f3', ink: '#1463f3', deep: '#0b1a55' },
  { key: 'teal', name: 'Teal', accent: '#13a8a8', ink: '#0b7a7a', deep: '#083f45' },
  { key: 'purple', name: 'Purple', accent: '#7c5cfa', ink: '#5a3ed8', deep: '#2a1868' },
];

const FEATURES = [
  {
    key: 'colours',
    icon: Palette,
    tone: 'blue',
    title: 'Your colours and logo',
    text: 'On every page; status colours stay locked.',
  },
  {
    key: 'cover',
    icon: ImageIcon,
    tone: 'green',
    title: 'Your cover and back cover',
    text: 'Ready-made designs or your own artwork.',
  },
  {
    key: 'marketing',
    icon: Megaphone,
    tone: 'amber',
    title: 'A marketing page inside',
    text: 'Your poster, your film, your founder’s note.',
  },
];

function BrandMark() {
  return (
    <svg className="beq-mark" viewBox="0 0 27 27" focusable="false">
      <path d="M13.37 13.38v13.38C5.99 26.76 0 20.77 0 13.38h13.37Z" opacity=".55" />
      <path d="M13.37 13.38H0V0c7.38 0 13.37 5.99 13.37 13.38Z" />
      <path d="M26.74 13.38c0 7.39-5.99 13.38-13.37 13.38 0-7.39 5.99-13.38 13.37-13.38Z" />
      <path d="M26.74 13.38c-7.38 0-13.37-5.99-13.37-13.38 7.38 0 13.37 5.99 13.37 13.38Z" opacity=".55" />
    </svg>
  );
}

function Brand() {
  return (
    <span className="beq-brand">
      <BrandMark />
      niroggyan
    </span>
  );
}

/* ---------- The three report pages (decorative, synthetic data) ---------- */

/*
 * The body on "Your Health at a Glance": a natural, warm medium skin tone,
 * shaded like a rounded form (darker at the edges, a soft highlight on the
 * left). The organs inside keep the brand tint.
 */
function GlanceFigure() {
  const uid = useId();
  const skin = `${uid}-skin`;
  const shine = `${uid}-shine`;
  const shade = `${uid}-shade`;
  const body =
    'M52 38h16l1 9c16 3 25 11 27 25l7 58c1 6-7 8-9 2l-10-50-3 40 4 104H73l-7-84h-12l-7 84H34l4-104-3-40-10 50c-2 6-10 4-9-2l7-58c2-14 11-22 27-25Z';
  return (
    <svg className="beq-glance__figure" viewBox="0 0 120 240" focusable="false">
      <defs>
        <linearGradient id={skin} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#b97b4f" />
          <stop offset="0.3" stopColor="#e0a676" />
          <stop offset="0.5" stopColor="#e8b285" />
          <stop offset="0.72" stopColor="#d6996a" />
          <stop offset="1" stopColor="#ad6f45" />
        </linearGradient>
        <radialGradient id={shine} cx="0.38" cy="0.3" r="0.45">
          <stop offset="0" stopColor="#fbdcbf" stopOpacity="0.6" />
          <stop offset="1" stopColor="#f6d2b4" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={shade} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#7a4a2e" stopOpacity="0" />
          <stop offset="0.75" stopColor="#7a4a2e" stopOpacity="0" />
          <stop offset="1" stopColor="#7a4a2e" stopOpacity="0.22" />
        </linearGradient>
      </defs>
      <g className="beq-glance__skin" fill={`url(#${skin})`} stroke="#94603f" strokeOpacity="0.35" strokeWidth="0.6">
        <ellipse cx="60" cy="22" rx="15" ry="18" />
        <path d={body} />
      </g>
      <g fill={`url(#${shade})`}>
        <path d={body} />
      </g>
      <path d="M52 38h16l1 9q-9 5-18 0Z" fill="#8f5a3a" opacity="0.35" />
      <g fill={`url(#${shine})`}>
        <ellipse cx="60" cy="22" rx="15" ry="18" />
        <path d={body} />
      </g>
      <g className="beq-glance__organs-art">
        <path d="M46 62c-6 2-9 12-8 24 1 8 6 10 10 7 3-3 3-12 3-20 0-7-2-12-5-11Z" />
        <path d="M74 62c6 2 9 12 8 24-1 8-6 10-10 7-3-3-3-12-3-20 0-7 2-12 5-11Z" />
        <path d="M44 98c6-4 18-4 22 0 3 3 0 9-6 10-7 1-15-1-17-4-1-2-1-4 1-6Z" />
        <ellipse cx="72" cy="104" rx="7" ry="5" />
        <path d="M49 116c3 8 19 8 22 0v12c-3 6-19 6-22 0Z" />
      </g>
    </svg>
  );
}

const ORGANS = [
  { key: 'heart', organ: 'heart', name: 'Heart', tone: 'red' },
  { key: 'lungs', organ: 'lungs', name: 'Lungs', tone: 'green' },
  { key: 'liver', organ: 'liver', name: 'Liver', tone: 'plum' },
];

const ORGANS_RIGHT = [
  { key: 'brain', organ: 'thyroid', tone: 'blue' },
  { key: 'kidney', organ: 'kidney', tone: 'green' },
];

const SUMMARY = [
  { key: 'tests', value: '32', label: 'Tests Analysed', tone: 'brand' },
  { key: 'normal', value: '28', label: 'Normal', tone: 'normal' },
  { key: 'borderline', value: '3', label: 'Borderline', tone: 'borderline' },
  { key: 'high', value: '1', label: 'High', tone: 'high' },
];

function GlancePage() {
  return (
    <div className="beq-page beq-page--glance" aria-hidden="true">
      <Brand />
      <p className="beq-glance__title">
        Your Health
        <br />
        at a Glance
      </p>
      <div className="beq-glance__body">
        <ul className="beq-glance__organs">
          {ORGANS.map(({ key, organ, name, tone }) => (
            <li key={key}>
              <span className={`beq-glance__organ is-${tone}`}>
                <Organ name={organ} size="1.7em" />
              </span>
              <span>
                {name}
                <small>Normal</small>
              </span>
            </li>
          ))}
        </ul>
        <GlanceFigure />
        <ul className="beq-glance__organs beq-glance__organs--right">
          {ORGANS_RIGHT.map(({ key, organ, tone }) => (
            <li key={key}>
              <span className={`beq-glance__organ is-${tone}`}>
                <Organ name={organ} size="1.7em" />
              </span>
            </li>
          ))}
        </ul>
      </div>
      <p className="beq-legend">
        <span className="is-normal">Normal</span>
        <span className="is-borderline">Borderline</span>
        <span className="is-high">High</span>
      </p>
      <p className="beq-glance__sub">Quick Summary</p>
      <ul className="beq-summary">
        {SUMMARY.map((s) => (
          <li key={s.key} className={`is-${s.tone}`}>
            <strong>{s.value}</strong>
            <span>{s.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const COVER_POINTS = [
  { key: 'insights', icon: HeartPulse, text: 'Personalised Insights' },
  { key: 'guidance', icon: Sprout, text: 'Actionable Guidance' },
  { key: 'proactive', icon: ShieldCheck, text: 'Proactive Health' },
];

const COVER_DETAILS = [
  { key: 'name', label: 'Name', value: 'Patient A' },
  { key: 'id', label: 'Patient ID', value: 'NG0000001' },
  { key: 'age', label: 'Age / Gender', value: '30 / Female' },
  { key: 'date', label: 'Collection Date', value: '09 Jun 2026' },
];

/* Two leaves, the cover's small emblem (the logo mark); filled from CSS. */
function CoverEmblem() {
  return (
    <svg className="beq-cover__emblem" viewBox="0 0 40 40" focusable="false">
      <path className="beq-cover__emblem-a" d="M19 36C9 33 4 24 7 12c9 1 15 7 15 16 0 3-1 6-3 8Z" />
      <path className="beq-cover__emblem-b" d="M21 36c8-2 13-9 12-19-8 1-13 6-13 13 0 2 0 4 1 6Z" />
      <path d="M19 35C16 27 12 20 8 14M21 35c3-6 6-11 11-16" stroke="#fff" strokeOpacity="0.5" strokeWidth="1.2" fill="none" />
    </svg>
  );
}

function CoverPage({ colour }) {
  const uid = useId();
  const clip = `${uid}-wave`;
  return (
    <div
      className="beq-page beq-page--cover"
      aria-hidden="true"
      data-cover-colour={colour.key}
      style={{ '--cover-accent': colour.accent, '--cover-ink': colour.ink, '--cover-deep': colour.deep }}
    >
      <svg className="beq-cover__art" viewBox="0 0 100 140" preserveAspectRatio="none" focusable="false">
        <defs>
          <clipPath id={clip} clipPathUnits="objectBoundingBox">
            <path d="M.36 0H1V1H.72C.52.86.2.74.18.52.16.32.4.18.36 0Z" />
          </clipPath>
        </defs>
        <path className="beq-cover__wash" d="M40 0H100V140H55C80 118 52 96 64 70 76 44 46 22 40 0Z" />
      </svg>
      <div className="beq-cover__scene" style={{ clipPath: `url(#${clip})` }}>
        <MountainLake className="beq-cover__lake" />
      </div>
      <LeafSprig className="beq-cover__sprig beq-cover__sprig--a" />
      <LeafSprig className="beq-cover__sprig beq-cover__sprig--b" />
      <CoverEmblem />
      <p className="beq-cover__title">
        Smart
        <br />
        Health Report
      </p>
      <p className="beq-cover__tag">
        Clear Insights
        <br />
        Healthier Tomorrows
      </p>
      <ul className="beq-cover__points">
        {COVER_POINTS.map(({ key, icon: Icon, text }) => (
          <li key={key}>
            <span>
              <Icon size="1.25em" strokeWidth={2} />
            </span>
            {text}
          </li>
        ))}
      </ul>
      <dl className="beq-cover__details">
        {COVER_DETAILS.map((d) => (
          <div key={d.key}>
            <dt>{d.label}</dt>
            <dd>{d.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

const BREAKDOWN = [
  { key: 'cardio', organ: 'heart', name: 'Cardiovascular', value: 90, tone: 'red', bar: 'green' },
  { key: 'metabolic', organ: 'stomach', name: 'Metabolic', value: 88, tone: 'amber', bar: 'green' },
  { key: 'liver', organ: 'liver', name: 'Liver', value: 94, tone: 'plum', bar: 'green' },
  { key: 'kidney', organ: 'kidney', name: 'Kidney', value: 91, tone: 'purple', bar: 'purple' },
  { key: 'thyroid', organ: 'thyroid', name: 'Thyroid', value: 89, tone: 'blue', bar: 'blue' },
];

/* The wellness score: a teal-to-green ring with the score in the middle. */
function WellnessRing() {
  const uid = useId();
  const grad = `${uid}-ring`;
  const r = 40;
  const c = 2 * Math.PI * r;
  return (
    <span className="beq-well__ring">
      <svg viewBox="0 0 100 100" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id={grad} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#13a8a8" />
            <stop offset="1" stopColor="#1fa463" />
          </linearGradient>
        </defs>
        <circle cx="50" cy="50" r={r} fill="none" stroke="#e6edf4" strokeWidth="7" />
        <circle
          cx="50"
          cy="50"
          r={r}
          fill="none"
          stroke={`url(#${grad})`}
          strokeWidth="7"
          strokeLinecap="round"
          strokeDasharray={`${c * 0.92} ${c}`}
          transform="rotate(-90 50 50)"
        />
      </svg>
      <strong aria-hidden="true">92</strong>
    </span>
  );
}

function WellnessPage() {
  return (
    <div className="beq-page beq-page--wellness">
      <div className="beq-well__head" aria-hidden="true">
        <p className="beq-well__title">
          Consolidated
          <br />
          Wellness Report
        </p>
        <Brand />
      </div>
      <p className="beq-well__sub" aria-hidden="true">
        Your Wellness Score
      </p>
      <div className="beq-well__score">
        <span className="beq-well__gauge" role="img" aria-label="Optimal: 92 out of 100">
          <WellnessRing />
          <span className="beq-well__optimal" aria-hidden="true">
            Optimal
          </span>
        </span>
        <p className="beq-well__note" aria-hidden="true">
          Great news! Your health parameters are in the ideal range. You have an optimal wellness score.
        </p>
      </div>
      <p className="beq-well__sub beq-well__sub--rule" aria-hidden="true">
        Your Score Breakdown
      </p>
      <ul className="beq-well__bars" aria-hidden="true">
        {BREAKDOWN.map(({ key, organ, name, value, tone, bar }) => (
          <li key={key}>
            <span className={`beq-well__icon is-${tone}`}>
              <Organ name={organ} size="1.2em" outline />
            </span>
            <span>{name}</span>
            <span className={`beq-well__track is-${bar}`}>
              <span style={{ width: `${value}%` }} />
            </span>
            <strong>{value}</strong>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function BrandEquity() {
  const [colourKey, setColourKey] = useState(BRAND_COLOURS[0].key);
  const colour = BRAND_COLOURS.find((c) => c.key === colourKey);

  return (
    <Screen id="product.brand-equity" title="Brand equity" className="beq">
      <div className="beq-decor" aria-hidden="true">
        <LeafSprig className="beq-decor__leaf beq-decor__leaf--top" />
        <LeafSprig className="beq-decor__leaf beq-decor__leaf--bottom" />
      </div>
      {/* The layout tuned to the photo (1176 px of content), scaled to the width available */}
      <FitToWidth designWidth={1176} minWidth={900}>
        <div className="beq-hero">
          <div className="beq-intro">
            <p className="pill pill--soft beq-chip">
              <span className="beq-chip__icon" aria-hidden="true">
                <ChartNoAxesColumnIncreasing size={16} strokeWidth={2.8} />
              </span>
              Brand equity
              <span className="beq-chip__stage">· Attract</span>
            </p>
            <h1 className="headline beq-headline">
              <em>Stand apart</em> from <br className="beq-headline__break" />
              every lab around you.
            </h1>
            <div className="beq-callout">
              <span className="icon-tile icon-tile--blue" aria-hidden="true">
                <ChartNoAxesColumnIncreasing size={26} strokeWidth={2.8} />
              </span>
              <p>
                <strong>Brand equity is why a patient picks your lab</strong> when every lab promises the same
                thing.
              </p>
            </div>

            <div className="beq-swatches">
              <p className="beq-swatches__label" id="beq-swatches-label">
                Try it in your colours
              </p>
              <div className="beq-swatches__row" role="group" aria-labelledby="beq-swatches-label">
                {BRAND_COLOURS.map((c) => {
                  const pressed = c.key === colourKey;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      className={`beq-swatch${pressed ? ' is-active' : ''}`}
                      style={{ '--swatch': c.accent }}
                      aria-pressed={pressed}
                      onClick={() => setColourKey(c.key)}
                      data-trapezium-id={`product.brand-equity.colour-${c.key}`}
                    >
                      <span className="beq-swatch__dot" aria-hidden="true">
                        {pressed && <Check size={12} strokeWidth={3} />}
                      </span>
                      {c.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="beq-preview" data-trapezium-state={`colour-${colour.key}`}>
            <p className="beq-applied" role="status">
              <span className="beq-applied__dot" aria-hidden="true" style={{ '--swatch': colour.accent }} />
              {`${colour.name} applied`}
            </p>
            <p className="sr-only">
              Three sample report pages: Your Health at a Glance, the Smart Health Report cover in the chosen
              brand colour, and a Consolidated Wellness Report with a wellness score of 92, Optimal.
            </p>
            {/* Decorative except the wellness gauge, which is one named image. Only the cover takes the colour. */}
            <div className="beq-stage">
              <GlancePage />
              <WellnessPage />
              <CoverPage colour={colour} />
            </div>
          </div>
        </div>

        <ul className="beq-features">
          {FEATURES.map(({ key, icon: Icon, tone, title, text }) => (
            <li key={key}>
              <span className={`beq-features__icon is-${tone}`} aria-hidden="true">
                <Icon size={30} strokeWidth={2} />
              </span>
              <span>
                <strong>{title}</strong>
                <span>{text}</span>
              </span>
            </li>
          ))}
        </ul>
      </FitToWidth>
    </Screen>
  );
}
